import { z } from 'zod';
export const platformSystemNotificationsContractVersion = 'platform.system-notifications.v1';
export const platformSystemNotificationsCapability = 'platform.system-notifications.v1';
export const platformSystemNotificationsEnqueueCapability = 'platform.system-notifications.enqueue.v1';
export const platformSystemNotificationRequestedEventName = 'platform.system-notification-requested.v1';
export const platformSystemNotificationSubscriberId = 'platform.subscribers.system-notification-requested';
export const platformSystemNotificationWorkerId = 'platform.subscribers.system-notification-requested';
export const platformSystemNotificationsReadOwnPermission = 'platform.system-notifications.read-own';
export const platformSystemNotificationsOperationsReadPermission = 'platform.system-notifications.operations.read';
const uuidSchema = z.string().uuid();
const timestampSchema = z.string().datetime({ offset: true });
function isCanonicalPlainText(value, maximum) {
    if (value !== value.trim() || value.replace(/\r\n?/g, '\n') !== value)
        return false;
    if (/[\u0000-\u0008\u000b-\u001f\u007f]/.test(value))
        return false;
    // PostgreSQL length(text) counts Unicode characters, not UTF-16 code units.
    // Keep the public/browser contract aligned with the stored-value constraint.
    const characterCount = Array.from(value).length;
    return characterCount >= 1 && characterCount <= maximum;
}
const plainTextSchema = (maximum) => z.string().refine((value) => isCanonicalPlainText(value, maximum), 'Expected canonical plain text between 1 and ' + maximum + ' characters.');
function hasUnsafePathSyntax(value) {
    if (/[\u0000-\u001f\u007f\\?#]/.test(value) || value.includes('//'))
        return true;
    return value.split('/').some((segment) => segment === '.' || segment === '..');
}
function hasCanonicalPercentEncoding(value) {
    for (let offset = 0; offset < value.length; offset += 1) {
        if (value[offset] !== '%')
            continue;
        const encoded = value.slice(offset + 1, offset + 3);
        if (encoded !== '20')
            return false;
        const decoded = String.fromCharCode(Number.parseInt(encoded, 16));
        if (/^[A-Za-z0-9\-._~]$/.test(decoded)
            || decoded === '%'
            || /[\u0000-\u001f\u007f\\?#/]/.test(decoded))
            return false;
        offset += 2;
    }
    return true;
}
function isCanonicalActionPath(value) {
    if (!/^\/[A-Za-z0-9._~!$&'()*+,;=:@%/-]+$/.test(value)
        || value.length > 500
        || hasUnsafePathSyntax(value)
        || !hasCanonicalPercentEncoding(value))
        return false;
    try {
        const decoded = decodeURIComponent(value);
        return !decoded.includes('%') && !hasUnsafePathSyntax(decoded);
    }
    catch {
        return false;
    }
}
export const platformSystemNotificationActionPathSchema = z.string().refine(isCanonicalActionPath, 'Expected a canonical same-origin absolute action path.');
export const platformSystemNotificationRequestedPayloadSchema = z.strictObject({
    requestId: uuidSchema,
});
export const platformSystemNotificationEntrySchema = z.strictObject({
    id: uuidSchema,
    createdAt: timestampSchema,
    sourceOccurredAt: timestampSchema,
    title: plainTextSchema(160),
    body: plainTextSchema(1_000),
    actionPath: platformSystemNotificationActionPathSchema,
    readAt: timestampSchema.nullable(),
});
export const platformSystemNotificationCursorValueSchema = z.strictObject({
    v: z.literal(1),
    createdAt: timestampSchema,
    id: uuidSchema,
});
function decodeBase64Url(value) {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/') +
        '='.repeat((4 - (value.length % 4)) % 4);
    return atob(base64);
}
function encodeBase64Url(value) {
    return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}
function isCanonicalBase64Url(value) {
    if (value.length < 1 || value.length > 1_024 || !/^[A-Za-z0-9_-]+$/.test(value))
        return false;
    try {
        const decoded = decodeBase64Url(value);
        const parsed = platformSystemNotificationCursorValueSchema.parse(JSON.parse(decoded));
        return encodeBase64Url(JSON.stringify(parsed)) === value;
    }
    catch {
        return false;
    }
}
export const platformSystemNotificationCursorSchema = z.string().refine(isCanonicalBase64Url, 'Expected a canonical system-notification cursor.');
export function encodePlatformSystemNotificationCursor(cursor) {
    return encodeBase64Url(JSON.stringify(platformSystemNotificationCursorValueSchema.parse(cursor)));
}
export function decodePlatformSystemNotificationCursor(cursor) {
    platformSystemNotificationCursorSchema.parse(cursor);
    return platformSystemNotificationCursorValueSchema.parse(JSON.parse(decodeBase64Url(cursor)));
}
export const platformSystemNotificationsListQuerySchema = z.strictObject({
    limit: z.union([
        z.number().int().min(1).max(50),
        z.string().regex(/^(?:[1-9]|[1-4][0-9]|50)$/).transform((value) => Number(value)),
    ]).default(30),
    cursor: platformSystemNotificationCursorSchema.optional(),
});
export const platformSystemNotificationMarkRequestSchema = z.strictObject({
    commandId: uuidSchema,
});
export const platformSystemNotificationsListResponseSchema = z.strictObject({
    contractVersion: z.literal(platformSystemNotificationsContractVersion),
    data: z.array(platformSystemNotificationEntrySchema).max(50),
    nextCursor: platformSystemNotificationCursorSchema.nullable(),
});
export const platformSystemNotificationsUnreadCountResponseSchema = z.strictObject({
    contractVersion: z.literal(platformSystemNotificationsContractVersion),
    data: z.strictObject({ unreadCount: z.number().int().nonnegative() }),
});
export const platformSystemNotificationMarkOneResponseSchema = z.strictObject({
    contractVersion: z.literal(platformSystemNotificationsContractVersion),
    data: z.strictObject({
        notificationId: uuidSchema,
        readAt: timestampSchema,
        updatedCount: z.union([z.literal(0), z.literal(1)]),
        idempotent: z.boolean(),
    }),
});
export const platformSystemNotificationMarkAllResponseSchema = z.strictObject({
    contractVersion: z.literal(platformSystemNotificationsContractVersion),
    data: z.strictObject({
        readAt: timestampSchema,
        cutoffAt: timestampSchema,
        updatedCount: z.number().int().min(0).max(500),
        hasMore: z.boolean(),
        idempotent: z.boolean(),
    }),
});
export const platformSystemNotificationHealthResponseSchema = z.strictObject({
    contractVersion: z.literal(platformSystemNotificationsContractVersion),
    data: z.strictObject({
        status: z.enum(['healthy', 'retrying', 'attention']),
        pendingCount: z.number().int().nonnegative(),
        retryingCount: z.number().int().nonnegative(),
        leasedCount: z.number().int().nonnegative(),
        deadLetterCount: z.number().int().nonnegative(),
        oldestPendingAt: timestampSchema.nullable(),
        lastMaterializedAt: timestampSchema.nullable(),
    }),
});
export const platformSystemNotificationConfigurationSchema = z.strictObject({
    unreadPollSeconds: z.number().int().min(15).max(300),
    openListPollSeconds: z.number().int().min(15).max(300),
    initialPageSize: z.number().int().min(10).max(50),
});
export const platformSystemNotificationDisplaySchema = z.strictObject({
    density: z.enum(['compact', 'comfortable']),
    showTimestamps: z.boolean(),
    groupByDate: z.boolean(),
    panelMaximumHeightPx: z.number().int().min(320).max(800),
});
export const platformSystemNotificationCopySchema = z.strictObject({
    bellLabel: plainTextSchema(240),
    unreadLabel: plainTextSchema(240).refine((value) => value.includes('{count}'), {
        message: 'Unread label must retain {count}.',
    }),
    panelTitle: plainTextSchema(240),
    closeAction: plainTextSchema(240),
    loadingLabel: plainTextSchema(240),
    emptyTitle: plainTextSchema(240),
    emptyDescription: plainTextSchema(240),
    unavailableLabel: plainTextSchema(240),
    retryAction: plainTextSchema(240),
    markReadAction: plainTextSchema(240),
    markAllAction: plainTextSchema(240),
    commandFailure: plainTextSchema(240),
    automaticRetry: plainTextSchema(240),
    recovered: plainTextSchema(240),
    operatorAttention: plainTextSchema(240),
    operationsStatusLabel: plainTextSchema(240),
    operationsPendingLabel: plainTextSchema(240),
    operationsRetryingLabel: plainTextSchema(240),
    operationsInProgressLabel: plainTextSchema(240),
    operationsNeedsAttentionLabel: plainTextSchema(240),
    operationsRefreshAction: plainTextSchema(240),
});
export const platformSystemNotificationDefaults = {
    configuration: {
        unreadPollSeconds: 30,
        openListPollSeconds: 15,
        initialPageSize: 30,
    },
    display: {
        density: 'comfortable',
        showTimestamps: true,
        groupByDate: true,
        panelMaximumHeightPx: 560,
    },
    copy: {
        bellLabel: 'Notifications',
        unreadLabel: '{count} unread notifications',
        panelTitle: 'Notifications',
        closeAction: 'Close notifications',
        loadingLabel: 'Loading notifications...',
        emptyTitle: "You're all caught up",
        emptyDescription: 'New system notifications will appear here.',
        unavailableLabel: 'Notifications are temporarily unavailable.',
        retryAction: 'Try again',
        markReadAction: 'Mark as read',
        markAllAction: 'Mark all as read',
        commandFailure: "We couldn't update that notification. Try again.",
        automaticRetry: 'Notification delivery is retrying automatically.',
        recovered: 'Notification delivery has recovered.',
        operatorAttention: 'Notification delivery needs administrator attention.',
        operationsStatusLabel: 'Notification delivery status',
        operationsPendingLabel: 'Pending',
        operationsRetryingLabel: 'Retrying',
        operationsInProgressLabel: 'In progress',
        operationsNeedsAttentionLabel: 'Needs attention',
        operationsRefreshAction: 'Refresh delivery status',
    },
};
export const platformSystemNotificationEnqueueInputSchema = z.strictObject({
    tenantId: uuidSchema,
    sourceEventId: uuidSchema,
    sourceEventName: z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+\.v[1-9][0-9]*$/),
    sourceEventVersion: z.number().int().positive(),
    sourceModuleId: z.string().regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/),
    sourceKind: z.string().regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/),
    sourceFactId: uuidSchema,
    sourceOccurredAt: timestampSchema,
    recipientMembershipId: uuidSchema,
    title: plainTextSchema(160),
    body: plainTextSchema(1_000),
    actionPath: platformSystemNotificationActionPathSchema,
});
export const platformSystemNotificationMaterializerInputSchema = z.strictObject({
    tenantId: uuidSchema,
    eventId: uuidSchema,
    requestId: uuidSchema,
});
//# sourceMappingURL=platform-system-notification.contract.js.map